import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { confirmDialog } from '../utils/confirm';
import { useAppContext } from '../contexts/AppContext';
import AccidentForm from '../components/AccidentForm';
import DocViewer from '../components/DocViewer';
import CustomSelect from '../components/CustomSelect';

const DAMAGE_COLORS = { minor: '#f59e0b', major: '#ef4444', total_loss: '#7c3aed' };
const DAMAGE_LABELS = { minor: 'Minor', major: 'Major', total_loss: 'Total Loss' };
const CLAIM_COLORS = { none: '#9ca3af', open: '#3b82f6', closed: '#10b981', rejected: '#ef4444' };
const FAULT_LABELS = { driver: 'Our Driver', third_party: 'Third Party', unknown: 'Unknown' };

const getFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/accidents/${filename}`;
};

const getAttachments = (a) => (a?.attachments?.length ? a.attachments : (a?.attachment?.filename ? [a.attachment] : []));
const getAttachmentPreview = (a) => {
  const list = getAttachments(a);
  return { urls: list.map((att) => getFileUrl(att.filename)), filenames: list.map((att) => att.originalName || att.filename) };
};

const Accidents = () => {
  const location = useLocation();
  const { searchQuery, isAdmin } = useAppContext();
  const [accidents, setAccidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccident, setEditingAccident] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterCompany, setFilterCompany] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.accidents.list();
      setAccidents(res.data);
      // Auto-select if navigated from Events page
      const targetId = location.state?.selectedAccidentId;
      if (targetId) {
        const found = res.data.find((a) => a._id === targetId);
        if (found) setSelected(found);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (data, file) => {
    if (editingAccident) {
      await apiEndpoints.accidents.update(editingAccident._id, data, file);
    } else {
      await apiEndpoints.accidents.create(data, file);
    }
    await load();
    setShowForm(false);
    setEditingAccident(null);
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Delete this accident record?')) return;
    await apiEndpoints.accidents.delete(id);
    setSelected(null);
    await load();
  };

  const formatDate = (v) => {
    if (!v) return '—';
    return new Date(v).toLocaleDateString();
  };

  const companies = [...new Set(accidents.map((a) => a.company).filter(Boolean))];
  const filtered = accidents.filter((a) => {
    if (filterCompany && a.company !== filterCompany) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = [a.plate, a.vehicleName, a.company, a.location, a.driverName, a.description].some((v) => v?.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });
  const totalRepairCost = filtered.reduce((s, a) => s + (a.repairCost || 0), 0);

  // Spend per company
  const byCompany = companies.reduce((acc, c) => {
    acc[c] = accidents.filter((a) => a.company === c).reduce((s, a) => s + (a.repairCost || 0), 0);
    return acc;
  }, {});

  if (loading) return <div className="page-loading">Loading accidents...</div>;

  if (selected) {
    const dmgColor = DAMAGE_COLORS[selected.damageLevel] || '#9ca3af';
    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to accidents
        </button>
        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{selected.plate}</span></div>
          <div className="vprofile-model">{selected.vehicleName || 'Accident'} — {formatDate(selected.date)}</div>
          <div className="vprofile-sub">{selected.company ? `${selected.company} • ` : ''}{selected.location || 'Location not specified'}</div>
        </div>
        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Accident Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingAccident(selected); setShowForm(true); }}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected._id)}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {[
                  ['Vehicle', selected.vehicleName || '—'],
                  ['Plate', selected.plate],
                  ['Company', selected.company || '—'],
                  ['Date', formatDate(selected.date)],
                  ['Location', selected.location || '—'],
                  ['Description', selected.description || '—'],
                  ['Driver', selected.driverName || '—'],
                  ['At Fault', FAULT_LABELS[selected.atFault] || '—'],
                  ['Repair Cost', selected.repairCost ? `${selected.repairCost.toLocaleString()} ${selected.currency}` : '—'],
                  ['Insurance Claim', selected.claimStatus?.toUpperCase() || '—'],
                  ['Notes', selected.notes || '—'],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="info-row">
                    <div className="info-lbl">{lbl}</div>
                    <div className="info-val">{val}</div>
                  </div>
                ))}
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Attachment{getAttachments(selected).length > 1 ? 's' : ''}</div>
                  <div className="info-val">
                    {getAttachments(selected).length > 0 ? (
                      <div style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                        {getAttachments(selected).map((att, idx) => (
                          <button key={att.filename || idx} onClick={() => setPreviewDoc({ ...getAttachmentPreview(selected), initialIndex: idx })} style={{ background: 'none', border: 'none', padding: 0, color: '#0077b6', fontSize: '13px', fontWeight: 500, cursor: 'pointer', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                            {att.originalName}
                          </button>
                        ))}
                      </div>
                    ) : <span style={{ color: 'var(--text-3)' }}>No file attached</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="detail-right">
            <div className="card">
              <div className="card-header"><div className="card-title">Summary</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '14px' }}>
                {getAttachments(selected).length > 0 ? (
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setPreviewDoc(getAttachmentPreview(selected))}>
                    Preview File{getAttachments(selected).length > 1 ? `s (${getAttachments(selected).length})` : ''}
                  </button>
                ) : (
                  <button className="btn btn-secondary" disabled style={{ opacity: 0.45, cursor: 'not-allowed', width: '100%' }}>No File Attached</button>
                )}
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: dmgColor }}>{DAMAGE_LABELS[selected.damageLevel]}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>damage level</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface-2)', borderRadius: '10px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600 }}>Insurance Claim</div>
                    <div style={{ fontWeight: 700, color: CLAIM_COLORS[selected.claimStatus], fontSize: '13px', marginTop: '2px' }}>{selected.claimStatus?.toUpperCase() || '—'}</div>
                  </div>
                </div>
                <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: '#0077b6' }}>{selected.repairCost ? `${selected.repairCost.toLocaleString()} ${selected.currency}` : '—'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>repair cost</div>
                </div>
                {selected.company && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600 }}>Company</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-1)', marginTop: '2px' }}>{selected.company}</div>
                  </div>
                )}
                {isAdmin && (
                  <button className="btn btn-secondary btn-sm" style={{ width: '100%' }} onClick={() => { setEditingAccident(selected); setShowForm(true); }}>Edit Record</button>
                )}
              </div>
            </div>
          </div>
        </div>
        <AccidentForm isOpen={showForm} onClose={() => { setShowForm(false); setEditingAccident(null); }} accident={editingAccident} onSave={handleSave} />
        <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} initialIndex={previewDoc?.initialIndex || 0} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Accidents</div>
          <div className="ph-sub">{filtered.length} record{filtered.length !== 1 ? 's' : ''} — total repair cost: {totalRepairCost.toLocaleString()} RON</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditingAccident(null); setShowForm(true); }}>
          + Report Accident
        </button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
        {['minor', 'major', 'total_loss'].map((lvl) => {
          const count = accidents.filter((a) => a.damageLevel === lvl).length;
          return (
            <div key={lvl} className="stat-card" style={{ borderTop: `3px solid ${DAMAGE_COLORS[lvl]}` }}>
              <div className="stat-label">{DAMAGE_LABELS[lvl].toUpperCase()}</div>
              <div className="stat-value">{count}</div>
              <div className="stat-meta">accidents</div>
            </div>
          );
        })}
        <div className="stat-card" style={{ borderTop: '3px solid #0077b6' }}>
          <div className="stat-label">TOTAL REPAIR COST</div>
          <div className="stat-value" style={{ fontSize: '20px' }}>{totalRepairCost.toLocaleString()}</div>
          <div className="stat-meta">RON</div>
        </div>
      </div>

      {/* Per-company spend */}
      {companies.length > 0 && (
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', marginTop: '12px' }}>
          {companies.map((c) => (
            <div key={c} className="stat-card" style={{ borderTop: '3px solid #6b7280', cursor: 'pointer' }} onClick={() => setFilterCompany(filterCompany === c ? '' : c)}>
              <div className="stat-label" style={{ fontSize: '10px' }}>{c.toUpperCase()}</div>
              <div className="stat-value" style={{ fontSize: '18px' }}>{byCompany[c].toLocaleString()}</div>
              <div className="stat-meta">RON repair costs</div>
            </div>
          ))}
        </div>
      )}

      {companies.length > 0 && (
        <div className="table-filters" style={{ marginTop: '16px' }}>
          <div className="filter-row">
            <CustomSelect name="filterCompany" className="form-select" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
              <option value="">All companies</option>
              {companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </CustomSelect>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No accident records yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE / VEHICLE', 'COMPANY', 'DATE', 'DAMAGE', 'REPAIR COST', 'CLAIM', 'AT FAULT', 'FILE', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(a)}>
                    <td style={{ padding: '12px' }}>
                      <span className="plate">{a.plate}</span>
                      {a.vehicleName && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '3px' }}>{a.vehicleName}</div>}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)', fontSize: '12px' }}>{a.company || '—'}</td>
                    <td style={{ padding: '12px' }}>{formatDate(a.date)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: DAMAGE_COLORS[a.damageLevel], backgroundColor: `${DAMAGE_COLORS[a.damageLevel]}18` }}>
                        {DAMAGE_LABELS[a.damageLevel]}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{a.repairCost ? `${a.repairCost.toLocaleString()} ${a.currency}` : '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ color: CLAIM_COLORS[a.claimStatus], fontWeight: 600, fontSize: '11px' }}>{a.claimStatus?.toUpperCase()}</span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{FAULT_LABELS[a.atFault]}</td>
                    <td style={{ padding: '12px' }} onClick={(e) => e.stopPropagation()}>
                      {getAttachments(a).length > 0 ? (
                        <button className="btn btn-ghost btn-xs" style={{ color: '#10b981' }} onClick={e => { e.stopPropagation(); setPreviewDoc(getAttachmentPreview(a)); }}>
                          Preview
                        </button>
                      ) : (
                        <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default' }}>Preview</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); setSelected(a); }}>View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AccidentForm isOpen={showForm} onClose={() => { setShowForm(false); setEditingAccident(null); }} accident={editingAccident} onSave={handleSave} />
      <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} initialIndex={previewDoc?.initialIndex || 0} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default Accidents;
